import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalFooter, ModalHeader } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { FieldLabel, MonoLabel } from '@/components/editorial';
import { Mic } from 'lucide-react';
import { PersonaGrid } from './PersonaGrid';
import { usePersonaPreferences } from '@/hooks/usePersonaPreferences';
import { useCompanionName } from '@/hooks/useCompanionName';
import type { PersonaId, VoiceLanguage } from '@/services/settingsService';
import { COMPANION_LANGUAGES, languageSelectorLabel } from '@/config/languages';

interface VoiceCharacterSelectorProps {
  open: boolean;
  mode: 'first-time' | 'settings';
  onSelect: (personaId: string) => void;
  onClose: () => void;
}

export function VoiceCharacterSelector({
  open,
  mode,
  onSelect,
  onClose,
}: VoiceCharacterSelectorProps) {
  const { t } = useTranslation(['chat', 'common']);
  const { preferences, characters, updatePreferences, isLoading } = usePersonaPreferences();
  const companionName = useCompanionName();
  const [selected, setSelected] = useState<PersonaId | undefined>();
  const [selectedLang, setSelectedLang] = useState<VoiceLanguage>(
    (preferences?.language as VoiceLanguage) ?? 'en',
  );

  // Sync selection when modal opens — only on open transition, not on preference changes mid-dialog
  useEffect(() => {
    if (open) setSelected(preferences?.persona as PersonaId | undefined);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && preferences?.language) {
      setSelectedLang(preferences.language as VoiceLanguage);
    }
  }, [open, preferences?.language]);

  const handleConfirm = async () => {
    if (!selected) return;
    await updatePreferences({ persona: selected, language: selectedLang });
    onSelect(selected);
    onClose();
  };

  const confirmLabel = mode === 'first-time' ? t('voiceSetup.firstTime.confirm') : t('voiceSetup.edit.confirm');
  const eyebrow = mode === 'first-time' ? t('voiceSetup.firstTime.eyebrow') : t('voiceSetup.edit.eyebrow');
  const headline =
    mode === 'first-time'
      ? t('voiceSetup.firstTime.title', { companion: companionName })
      : t('voiceSetup.edit.title', { companion: companionName });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl flex flex-col p-0 gap-0 max-h-[90vh]">
        <ModalHeader icon={Mic} eyebrow={eyebrow} title={headline} />

        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          {isLoading ? (
            <p className="font-serif text-body text-stevens-ink-3 italic py-6">
              {t('voiceSetup.loading')}
            </p>
          ) : (
            <>
              <div className="mb-4">
                <FieldLabel htmlFor="voice-language-select" className="mb-1.5">{t('voiceSetup.languageLabel')}</FieldLabel>
                <select
                  id="voice-language-select"
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value as VoiceLanguage)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-body"
                >
                  {COMPANION_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>{languageSelectorLabel(lang)}</option>
                  ))}
                </select>
                <p className="text-label text-stevens-ink-3 mt-1 italic">
                  {t('voiceSetup.languageBeta')}
                </p>
              </div>
              <PersonaGrid characters={characters} selectedId={selected} onSelect={setSelected} showPreview />
            </>
          )}
        </div>

        <ModalFooter
          lead={
            <MonoLabel className="text-[11px]">
              {t('voiceSetup.changeLater')}
            </MonoLabel>
          }
        >
          {mode === 'settings' && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
          )}
          <Button
            size="sm"
            variant="brand"
            onClick={handleConfirm}
            disabled={!selected || isLoading}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}
